import { __decorate } from "tslib";
import React from 'react';
import { observer } from 'mobx-react';
import { computed, isArrayLike } from 'mobx';
import classNames from 'classnames';
import { Select } from '../select/Select';
import ObserverRadio from '../radio/Radio';
import ObserverCheckBox from '../check-box/CheckBox';
import autobind from '../_util/autobind';
import Option from '../option/Option';
import OptGroup from '../option/OptGroup';
import { $l } from '../locale-context';
import { LabelLayout } from '../form/enum';
import TextField from '../text-field';
import Icon from '../icon';
import Button from '../button/Button';
import { FuncType } from '../button/enum';
import { OTHER_OPTION_PROPS } from '../option/normalizeOptions';
const GroupIdGen = (function* (id) {
    while (true) {
        yield `__group-${id++}__`;
    }
})(1);
export class SelectBox extends Select {
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('SelectBox', label ? 'value_missing' : 'value_missing_no_label', {
                label,
            }),
        };
    }
    get searchable() {
        const { searchable = this.getContextConfig('selectBoxSearchable') } = this.observableProps;
        return !!searchable;
    }
    get name() {
        return this.observableProps.name || GroupIdGen.next().value;
    }
    get mode() {
        return this.props.mode;
    }
    isSearchFieldInPopup() {
        return false;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'vertical',
        ]);
    }
    getClassName(...props) {
        const { prefixCls, props: { vertical }, } = this;
        return super.getClassName({
            [`${prefixCls}-vertical`]: vertical,
        }, ...props);
    }
    getWrapperClassNamesExcludeOutput(prefixCls, _) {
        return super.getWrapperClassNamesExcludeOutput(prefixCls, false);
    }
    renderSearcher() {
        if (this.searchable) {
            const { placeholder } = this.props;
            return (React.createElement(TextField, { prefix: React.createElement(Icon, { type: "search" }), placeholder: placeholder, value: this.text, onInput: this.handleInput, labelLayout: this.labelLayout }));
        }
    }
    getOptionOtherProps(_) {
        return {};
    }
    renderWrapper() {
        const { name, options, filteredOptions, textField, valueField, readOnly, disabled, mode } = this;
        const { autoFocus, onOption, optionRenderer, optionsFilter } = this.props;
        const highlight = this.getDisplayProp('highlight');
        const items = filteredOptions.reduce((arr, record, index, data) => {
            if (!optionsFilter || optionsFilter(record, index, data)) {
                const optionProps = onOption({ dataSet: options, record });
                const text = record.get(textField);
                const value = record.get(valueField);
                const checked = this.isChecked(this.getValue(), value);
                const children = optionRenderer
                    ? optionRenderer({ dataSet: options, record, text, value })
                    : text;
                const itemProps = {
                    ...record.get(OTHER_OPTION_PROPS),
                    key: index,
                    dataSet: null,
                    record: null,
                    value,
                    checked,
                    name,
                    onChange: this.handleItemChange,
                    children,
                    autoFocus: autoFocus && index === 0,
                    readOnly,
                    disabled,
                    mode,
                    noValidate: true,
                    labelLayout: LabelLayout.none,
                    highlight,
                    ...this.getOptionOtherProps(checked),
                };
                arr.push(this.renderItem(optionProps ? {
                    ...optionProps,
                    ...itemProps,
                    className: classNames(optionProps.className, itemProps.className),
                    style: {
                        ...optionProps.style,
                        ...itemProps.style,
                    },
                    disabled: itemProps.disabled || optionProps.disabled,
                } : itemProps));
            }
            return arr;
        }, []);
        return (React.createElement("span", Object.assign({ key: "wrapper" }, this.getWrapperProps()),
            this.renderSearcher(),
            this.renderSelectAll(),
            this.renderSelectItems(items),
            this.renderFloatLabel(),
            options.paging && options.currentPage < options.totalPage && (React.createElement(Button, { funcType: FuncType.flat, icon: "more_horiz", onClick: this.handleQueryMore }))));
    }
    renderSelectItems(items) {
        const className = this.getClassName();
        const Element = this.context.formNode ? 'div' : 'form';
        return (React.createElement(Element, { className: className, onMouseEnter: this.handleMouseEnter, onMouseLeave: this.handleMouseLeave }, items));
    }
    handleQueryMore() {
        const { options } = this;
        options.queryMore(options.currentPage + 1);
    }
    handleInput(e) {
        const { value } = e.target;
        this.setText(value);
    }
    handleItemChange(value, oldValue) {
        if (this.multiple) {
            const values = this.getValues();
            if (!value) {
                values.splice(values.indexOf(oldValue), 1);
            }
            else {
                values.push(value);
            }
            this.setValue(values);
        }
        else {
            this.setValue(value);
        }
    }
    isChecked(value, checkedValue) {
        if (isArrayLike(value)) {
            return value.indexOf(checkedValue) !== -1;
        }
        return value === checkedValue;
    }
    renderItem(props) {
        if (this.multiple) {
            return React.createElement(ObserverCheckBox, Object.assign({}, props));
        }
        return React.createElement(ObserverRadio, Object.assign({}, props));
    }
}
SelectBox.displayName = 'SelectBox';
SelectBox.defaultProps = {
    ...Select.defaultProps,
    suffixCls: 'select-box',
    vertical: false,
    selectAllButton: false,
    checkValueOnOptionsChange: false,
};
SelectBox.Option = Option;
SelectBox.OptGroup = OptGroup;
// eslint-disable-next-line camelcase
SelectBox.__IS_IN_CELL_EDITOR = true;
__decorate([
    computed
], SelectBox.prototype, "name", null);
__decorate([
    autobind
], SelectBox.prototype, "renderSelectItems", null);
__decorate([
    autobind
], SelectBox.prototype, "handleQueryMore", null);
__decorate([
    autobind
], SelectBox.prototype, "handleInput", null);
__decorate([
    autobind
], SelectBox.prototype, "handleItemChange", null);
let ObserverSelectBox = class ObserverSelectBox extends SelectBox {
};
ObserverSelectBox.defaultProps = SelectBox.defaultProps;
ObserverSelectBox.Option = Option;
ObserverSelectBox.OptGroup = OptGroup;
// eslint-disable-next-line camelcase
ObserverSelectBox.__IS_IN_CELL_EDITOR = true;
ObserverSelectBox = __decorate([
    observer
], ObserverSelectBox);
export default ObserverSelectBox;
